import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './TrolleyEngine.module.css';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

const StickFigure = ({ cx, cy, isAnimating = false }) => (
  <motion.g
    animate={isAnimating ? { scale: [1, 1.02, 1] } : {}}
    transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
    style={{ transformOrigin: `${cx}px ${cy}px` }}
  >
    {/* Head */}
    <circle cx={cx} cy={cy - 12} r="5" className={styles.figureHead} />
    {/* Body */}
    <line x1={cx} y1={cy - 7} x2={cx} y2={cy + 8} className={styles.figureLine} />
    {/* Arms */}
    <line x1={cx - 6} y1={cy - 2} x2={cx + 6} y2={cy - 2} className={styles.figureLine} />
    {/* Legs */}
    <line x1={cx} y1={cy + 8} x2={cx - 5} y2={cy + 18} className={styles.figureLine} />
    <line x1={cx} y1={cy + 8} x2={cx + 5} y2={cy + 18} className={styles.figureLine} />
  </motion.g>
);

const TrolleyEngine = ({ gamifiedQuestion, onAnswer, accent = '#e63946' }) => {
  const [selected, setSelected] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [trolleyAnimating, setTrolleyAnimating] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 5) || [], [gamifiedQuestion?.options]);
  const selectedOption = options.find((option) => option.label === selected);

  useEffect(() => {
    // Scene entry animation
    const timer = setTimeout(() => setSceneReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const choose = (label) => {
    if (selected || !sceneReady) return;
    setSelected(label);
    setTrolleyAnimating(true);

    // Minimum 800ms before showing outcome
    setTimeout(() => {
      setShowOutcome(true);
    }, 800);
  };

  const getTrolleyAnimation = (label) => {
    if (!label || !trolleyAnimating) return { x: 0, y: 0 };

    // Animate trolley along chosen track
    if (label === 'A') {
      // Upper track - fewer people
      return { x: 320, y: -50 };
    } else if (label === 'B') {
      // Lower track - more people
      return { x: 320, y: 40 };
    } else {
      // C, D, E - secondary panel, trolley stops at fork
      return { x: 160, y: 0 };
    }
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        <motion.svg
          viewBox="0 0 600 280"
          className={styles.sceneSvg}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Main track line (left side) */}
          <motion.line
            x1="40" y1="140" x2="200" y2="140"
            className={styles.track}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />

          {/* Upper diverging track */}
          <motion.line
            x1="200" y1="140" x2="480" y2="80"
            className={styles.track}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          />

          {/* Lower diverging track */}
          <motion.line
            x1="200" y1="140" x2="480" y2="200"
            className={styles.track}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          />

          {/* Lever figure at fork point */}
          <motion.g
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2, type: 'spring', stiffness: 200 }}
          >
            <StickFigure cx={200} cy={110} isAnimating />
            {/* Lever */}
            <line x1="200" y1="125" x2="220" y2="145" className={styles.lever} />
            <circle cx="220" cy="145" r="3" className={styles.leverKnob} />
          </motion.g>

          {/* Upper track - 1 stick figure */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
          >
            <StickFigure cx={440} cy={85} isAnimating={!trolleyAnimating} />
          </motion.g>

          {/* Lower track - 5 stick figures in a row */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <StickFigure cx={380} cy={208} isAnimating={!trolleyAnimating} />
            <StickFigure cx={400} cy={206} isAnimating={!trolleyAnimating} />
            <StickFigure cx={420} cy={204} isAnimating={!trolleyAnimating} />
            <StickFigure cx={440} cy={202} isAnimating={!trolleyAnimating} />
            <StickFigure cx={460} cy={200} isAnimating={!trolleyAnimating} />
          </motion.g>

          {/* Trolley */}
          <motion.g
            initial={{ x: -150, y: 106 }}
            animate={trolleyAnimating ? getTrolleyAnimation(selected) : { x: 0, y: 0 }}
            transition={{ type: 'spring', stiffness: 60, damping: 20, duration: 0.8 }}
          >
            {/* Trolley car body */}
            <rect x="40" y="115" width="120" height="60" rx="8" className={styles.trolley} />
            {/* Trolley window/trim */}
            <rect x="50" y="125" width="40" height="25" rx="4" className={styles.trolleyWindow} />
            {/* Wheels */}
            <circle cx="65" cy="180" r="8" className={styles.wheel} />
            <circle cx="135" cy="180" r="8" className={styles.wheel} />
            {/* Driver */}
            <circle cx="95" cy="145" r="6" className={styles.driver} />
          </motion.g>

          {/* Track labels */}
          <motion.text x="460" y="60" className={styles.trackLabel} textAnchor="middle"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}>
            Track A (1 person)
          </motion.text>
          <motion.text x="460" y="235" className={styles.trackLabel} textAnchor="middle"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }}>
            Track B (5 people)
          </motion.text>
        </motion.svg>

        {/* Consequence overlay for C/D/E selections */}
        <AnimatePresence>
          {selected && ['C', 'D', 'E'].includes(selected) && (
            <motion.div
              className={styles.secondaryPanel}
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 100, damping: 20 }}
            >
              <div className={styles.panelContent}>
                <span className={styles.panelIcon}>⚡</span>
                <span className={styles.panelText}>Alternative action selected</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Bottom */}
      <div className={styles.optionsArea}>
        {LETTERS.map((label, index) => {
          const option = options[index];
          if (!option) return null;

          return (
            <motion.button
              key={label}
              type="button"
              className={`${styles.choiceBtn} ${selected === label ? styles.selected : ''}`}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: selected && selected !== label ? 0.3 : 1 }}
              transition={{ delay: 1.8 + index * 0.1, type: 'spring', stiffness: 100 }}
              whileHover={!selected ? { y: -2, boxShadow: `0 8px 24px ${accent}30` } : {}}
              whileTap={!selected ? { scale: 0.98 } : {}}
              onClick={() => choose(label)}
              disabled={!!selected}
            >
              <span className={styles.choiceLabel}>{label}</span>
              <span className={styles.choiceText}>{option.actionText || option.text}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selected === 'A' || selected === 'B' ? 'warning' : 'neutral'}
        message={selectedOption?.consequence || 'Moral decision captured.'}
        duration={1700}
        onDone={() => {
          setShowOutcome(false);
          if (selected) onAnswer(selected);
        }}
      />
    </div>
  );
};

export default TrolleyEngine;
